import { useEffect, useState, useRef } from "react";
import { useApp } from "@/context/AppContext";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { SEOHead } from "@/components/SEOHead";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Plus, MoreHorizontal, Upload, Loader2 } from "lucide-react";

const STATUS_OPTIONS = ["pending", "confirmed", "preparing", "out_for_delivery", "delivered", "cancelled"];

// Reusable image picker: file upload OR paste URL.
// Calls onChange(url) with the optimized images.weserv.nl URL.
export const ImageUpload = ({ value, onChange, testIdPrefix = "img" }) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image must be under 8 MB");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/upload/image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(r.data.url);
      toast.success("Image uploaded & optimized");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-2" data-testid={`${testIdPrefix}-uploader`}>
      <div className="flex gap-3 items-start">
        {value ? (
          <img
            src={value}
            alt="preview"
            className="w-20 h-20 object-cover border"
            style={{ borderColor: "var(--line)" }}
            data-testid={`${testIdPrefix}-preview`}
          />
        ) : (
          <div
            className="w-20 h-20 border flex items-center justify-center text-xs"
            style={{ borderColor: "var(--line)", color: "var(--ink-soft)" }}
          >
            No image
          </div>
        )}
        <div className="flex-1 space-y-2">
          <input
            type="file"
            accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
            ref={fileInputRef}
            className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])}
            data-testid={`${testIdPrefix}-file-input`}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="btn-secondary inline-flex items-center gap-2 text-sm"
            data-testid={`${testIdPrefix}-upload-btn`}
          >
            {uploading
              ? <><Loader2 size={14} strokeWidth={1.5} className="animate-spin" /> Uploading…</>
              : <><Upload size={14} strokeWidth={1.5} /> Upload image</>}
          </button>
          <input
            className="input-field text-xs"
            placeholder="…or paste image URL"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            data-testid={`${testIdPrefix}-url-input`}
          />
        </div>
      </div>
      <p className="text-[11px]" style={{ color: "var(--ink-soft)" }}>
        PNG / JPG / WEBP up to 8 MB. Auto-optimized for fast loading.
      </p>
    </div>
  );
};

export default function Admin() {
  const { user } = useApp();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [customs, setCustoms] = useState([]);
  const [hampers, setHampers] = useState([]);

  useEffect(() => {
    if (user === null) return;
    if (!user || !user.is_admin) {
      navigate("/login");
    }
  }, [user, navigate]);

  const refresh = () => {
    api.get("/orders").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/custom-cakes").then((r) => setCustoms(r.data)).catch(() => {});
    api.get("/gift-hampers/inquiries").then((r) => setHampers(r.data)).catch(() => {});
  };

  useEffect(() => {
    if (user?.is_admin) refresh();
  }, [user]);

  if (!user?.is_admin) return null;

  const setStatus = async (orderId, status) => {
    try {
      await api.patch(`/orders/${orderId}/status`, { status });
      toast.success(`Status updated → ${status}`);
      refresh();
    } catch (err) {
      toast.error("Failed to update");
    }
  };

  const stats = {
    orders: orders.length,
    pending: orders.filter((o) => o.status === "pending").length,
    revenue: orders.filter((o) => o.payment_status === "paid" || o.payment_status === "cod").reduce((s, o) => s + o.total, 0),
    products: products.length,
  };

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-12" data-testid="admin-page">
      <SEOHead title="Admin Dashboard | Cocoamaya" noindex />
      <div className="flex justify-between items-end flex-wrap gap-4 mb-10">
        <div>
          <p className="eyebrow mb-2">Control room</p>
          <h1 className="font-display text-5xl tracking-tight">Admin Dashboard</h1>
        </div>
        <p className="text-sm" style={{ color: "var(--ink-soft)" }}>Signed in as {user.name}</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
        {[
          { label: "Orders", value: stats.orders, key: "orders" },
          { label: "Pending", value: stats.pending, key: "pending" },
          { label: "Revenue", value: `₹${Math.round(stats.revenue)}`, key: "rev" },
          { label: "Products", value: stats.products, key: "prod" },
        ].map((s) => (
          <div key={s.key} className="p-6 border" style={{ borderColor: "var(--line)" }} data-testid={`stat-${s.key}`}>
            <p className="eyebrow mb-2">{s.label}</p>
            <p className="font-display text-4xl">{s.value}</p>
          </div>
        ))}
      </div>

      <Tabs defaultValue="orders" className="w-full">
        <TabsList className="mb-8 bg-transparent p-0 border-b" style={{ borderColor: "var(--line)" }}>
          <TabsTrigger value="orders" data-testid="tab-orders" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[var(--ink)] data-[state=active]:bg-transparent">Orders</TabsTrigger>
          <TabsTrigger value="products" data-testid="tab-products" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[var(--ink)] data-[state=active]:bg-transparent">Products</TabsTrigger>
          <TabsTrigger value="customs" data-testid="tab-customs" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[var(--ink)] data-[state=active]:bg-transparent">Custom Cakes</TabsTrigger>
          <TabsTrigger value="hampers" data-testid="tab-hampers" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[var(--ink)] data-[state=active]:bg-transparent">Hamper Inquiries</TabsTrigger>
        </TabsList>

        <TabsContent value="orders">
          <div className="overflow-x-auto border" style={{ borderColor: "var(--line)" }}>
            <table className="w-full text-sm" data-testid="orders-table">
              <thead style={{ background: "var(--bg-soft)" }}>
                <tr className="text-left">
                  <th className="p-4 eyebrow">Order</th>
                  <th className="p-4 eyebrow">Customer</th>
                  <th className="p-4 eyebrow">Total</th>
                  <th className="p-4 eyebrow">Pay</th>
                  <th className="p-4 eyebrow">Status</th>
                  <th className="p-4 eyebrow"></th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.id} className="border-t" style={{ borderColor: "var(--line)" }} data-testid={`order-row-${o.id}`}>
                    <td className="p-4 font-mono text-xs">{o.id.slice(0, 8)}…</td>
                    <td className="p-4">
                      <p>{o.customer_name}</p>
                      <p className="text-xs" style={{ color: "var(--ink-soft)" }}>{o.customer_email}</p>
                    </td>
                    <td className="p-4">₹{Math.round(o.total)}</td>
                    <td className="p-4 capitalize text-xs">{o.payment_method} · {o.payment_status}</td>
                    <td className="p-4">
                      <span
                        className="px-3 py-1 text-xs"
                        style={{
                          background: o.status === "delivered" ? "var(--success)" : "var(--bg-soft)",
                          color: o.status === "delivered" ? "var(--bg)" : "var(--ink)",
                        }}
                      >
                        {o.status}
                      </span>
                    </td>
                    <td className="p-4 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button data-testid={`order-action-${o.id}`}><MoreHorizontal size={16} strokeWidth={1.5} /></button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuLabel>Update status</DropdownMenuLabel>
                          {STATUS_OPTIONS.map((s) => (
                            <DropdownMenuItem key={s} onClick={() => setStatus(o.id, s)} data-testid={`set-status-${s}-${o.id}`}>
                              {s}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
                {orders.length === 0 && (
                  <tr><td colSpan={6} className="p-12 text-center" style={{ color: "var(--ink-soft)" }}>No orders yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="products">
          <div className="flex justify-end mb-4">
            <ProductDialog onSaved={refresh} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {products.map((p) => (
              <div key={p.id} className="border p-4 flex gap-4" style={{ borderColor: "var(--line)" }} data-testid={`prod-row-${p.id}`}>
                <img src={p.image} alt={p.name} className="w-20 h-20 object-cover rounded-sm" />
                <div className="flex-1">
                  <p className="eyebrow text-[10px]">{p.category}</p>
                  <p className="font-medium">{p.name}</p>
                  <p className="text-sm" style={{ color: "var(--ink-soft)" }}>
                    {p.variants && p.variants.length
                      ? p.variants.map((v) => `${v.label} · ₹${Math.round(v.price)}`).join("  ·  ")
                      : `₹${Math.round(p.price)}`}
                  </p>
                  <div className="flex flex-wrap gap-3 mt-2">
                    <EditPriceButton product={p} onSaved={refresh} />
                    <ReplaceImageButton product={p} onSaved={refresh} />
                    <button
                      className="text-xs underline"
                      onClick={async () => { await api.delete(`/products/${p.id}`); toast.success("Deleted"); refresh(); }}
                      data-testid={`delete-prod-${p.id}`}
                    >Delete</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="customs">
          <div className="overflow-x-auto border" style={{ borderColor: "var(--line)" }}>
            <table className="w-full text-sm" data-testid="customs-table">
              <thead style={{ background: "var(--bg-soft)" }}>
                <tr className="text-left">
                  <th className="p-4 eyebrow">Customer</th>
                  <th className="p-4 eyebrow">Size</th>
                  <th className="p-4 eyebrow">Flavor</th>
                  <th className="p-4 eyebrow">Delivery</th>
                  <th className="p-4 eyebrow">Notes</th>
                </tr>
              </thead>
              <tbody>
                {customs.map((c) => (
                  <tr key={c.id} className="border-t" style={{ borderColor: "var(--line)" }} data-testid={`custom-row-${c.id}`}>
                    <td className="p-4">
                      <p>{c.customer_name}</p>
                      <p className="text-xs" style={{ color: "var(--ink-soft)" }}>{c.customer_email}</p>
                    </td>
                    <td className="p-4 capitalize">{c.cake_size}</td>
                    <td className="p-4">{c.flavor} · {c.frosting}</td>
                    <td className="p-4 text-xs">{new Date(c.delivery_date).toLocaleDateString()}</td>
                    <td className="p-4 text-xs max-w-[300px] truncate" title={c.special_requests}>{c.special_requests || "—"}</td>
                  </tr>
                ))}
                {customs.length === 0 && (
                  <tr><td colSpan={5} className="p-12 text-center" style={{ color: "var(--ink-soft)" }}>No custom cake requests.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="hampers">
          <div className="overflow-x-auto border" style={{ borderColor: "var(--line)" }}>
            <table className="w-full text-sm" data-testid="hampers-table">
              <thead style={{ background: "var(--bg-soft)" }}>
                <tr className="text-left">
                  <th className="p-4 eyebrow">Customer</th>
                  <th className="p-4 eyebrow">Occasion</th>
                  <th className="p-4 eyebrow">Qty</th>
                  <th className="p-4 eyebrow">Date</th>
                  <th className="p-4 eyebrow">Location</th>
                  <th className="p-4 eyebrow">Message</th>
                </tr>
              </thead>
              <tbody>
                {hampers.map((h) => (
                  <tr key={h.id} className="border-t" style={{ borderColor: "var(--line)" }} data-testid={`hamper-row-${h.id}`}>
                    <td className="p-4">
                      <p>{h.first_name} {h.last_name}</p>
                      <p className="text-xs" style={{ color: "var(--ink-soft)" }}>{h.email}</p>
                      <p className="text-xs" style={{ color: "var(--ink-soft)" }}>+91 {h.phone}</p>
                    </td>
                    <td className="p-4 text-xs">{h.occasion}</td>
                    <td className="p-4 text-xs">{h.quantity_range}</td>
                    <td className="p-4 text-xs">{new Date(h.delivery_date).toLocaleDateString()}</td>
                    <td className="p-4 text-xs">{h.delivery_location}</td>
                    <td className="p-4 text-xs max-w-[280px] truncate" title={h.message}>{h.message || "—"}</td>
                  </tr>
                ))}
                {hampers.length === 0 && (
                  <tr><td colSpan={6} className="p-12 text-center" style={{ color: "var(--ink-soft)" }}>No hamper inquiries yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

const ProductDialog = ({ onSaved }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "", description: "", category: "breads", price: 5,
    image: "", featured: false, stock: 100,
  });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/products", { ...form, price: parseFloat(form.price), stock: parseInt(form.stock) });
      toast.success("Product added");
      setOpen(false);
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="btn-primary inline-flex" data-testid="add-product-btn">
          <Plus size={16} strokeWidth={1.5} /> Add product
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">New product</DialogTitle>
          <DialogDescription className="text-sm" style={{ color: "var(--ink-soft)" }}>
            Add a new item to the catalogue. Upload a photo or paste an image URL.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <input className="input-field" placeholder="Name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="np-name" />
          <textarea className="input-field" placeholder="Description" required rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="np-desc" />
          <div className="grid grid-cols-2 gap-3">
            <select className="input-field" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="np-cat">
              <option value="breads">Breads</option>
              <option value="pastries">Pastries</option>
              <option value="cakes">Cakes</option>
              <option value="cookies">Cookies</option>
            </select>
            <input className="input-field" type="number" step="0.01" placeholder="Price" required value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="np-price" />
          </div>
          <ImageUpload
            value={form.image}
            onChange={(url) => setForm({ ...form, image: url })}
            testIdPrefix="np-img"
          />
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} data-testid="np-featured" />
            Mark as featured
          </label>
          <button className="btn-primary w-full justify-center" data-testid="np-submit">Save product</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};


const ReplaceImageButton = ({ product, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [pendingUrl, setPendingUrl] = useState(product.image || "");

  const save = async () => {
    if (!pendingUrl) {
      toast.error("Select or upload an image first");
      return;
    }
    try {
      await api.patch(`/products/${product.id}/image`, { image: pendingUrl });
      toast.success("Image updated");
      setOpen(false);
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) setPendingUrl(product.image || ""); }}>
      <DialogTrigger asChild>
        <button className="text-xs underline" data-testid={`replace-img-${product.id}`}>
          Replace image
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Replace image — {product.name}</DialogTitle>
          <DialogDescription className="text-sm" style={{ color: "var(--ink-soft)" }}>
            Upload a new photo or paste a URL. Images are auto-optimized for fast loading.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <ImageUpload
            value={pendingUrl}
            onChange={setPendingUrl}
            testIdPrefix={`ri-${product.id}`}
          />
          <div className="flex justify-end gap-2">
            <button
              type="button"
              className="btn-secondary text-sm"
              onClick={() => setOpen(false)}
              data-testid={`ri-${product.id}-cancel`}
            >Cancel</button>
            <button
              type="button"
              className="btn-primary text-sm"
              onClick={save}
              data-testid={`ri-${product.id}-save`}
            >Save</button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const EditPriceButton = ({ product, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [basePrice, setBasePrice] = useState(product.price ?? 0);
  const [variants, setVariants] = useState(product.variants || []);
  const [saving, setSaving] = useState(false);

  const openWith = (o) => {
    setOpen(o);
    if (o) {
      setBasePrice(product.price ?? 0);
      setVariants(product.variants ? product.variants.map((v) => ({ ...v })) : []);
    }
  };

  const setVariant = (i, key, val) => {
    setVariants((vs) => vs.map((v, idx) => (idx === i ? { ...v, [key]: val } : v)));
  };
  const addVariant = () => setVariants((vs) => [...vs, { label: "", price: basePrice }]);
  const removeVariant = (i) => setVariants((vs) => vs.filter((_, idx) => idx !== i));

  const save = async () => {
    const priceNum = Number(basePrice);
    if (!Number.isFinite(priceNum) || priceNum < 0) {
      toast.error("Base price must be a positive number");
      return;
    }
    const cleaned = variants
      .filter((v) => v.label && v.label.trim())
      .map((v) => ({ label: v.label.trim(), price: Number(v.price) }));
    for (const v of cleaned) {
      if (!Number.isFinite(v.price) || v.price < 0) {
        toast.error(`Variant "${v.label}" needs a valid price`);
        return;
      }
    }
    setSaving(true);
    try {
      await api.put(`/products/${product.id}`, {
        name: product.name,
        description: product.description,
        category: product.category,
        image: product.image,
        featured: !!product.featured,
        price: priceNum,
        variants: cleaned,
      });
      toast.success("Pricing updated");
      setOpen(false);
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={openWith}>
      <DialogTrigger asChild>
        <button className="text-xs underline" data-testid={`edit-price-${product.id}`}>
          Edit price
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Edit pricing — {product.name}</DialogTitle>
          <DialogDescription className="text-sm" style={{ color: "var(--ink-soft)" }}>
            Set the base price and any size variants shown to customers.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-5">
          <label className="block">
            <span className="block text-sm mb-1.5">Base price (₹)</span>
            <input
              type="number"
              min="0"
              step="1"
              className="input-field"
              value={basePrice}
              onChange={(e) => setBasePrice(e.target.value)}
              data-testid={`ep-${product.id}-base`}
            />
          </label>

          <div>
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm">Variants (size · price)</span>
              <button
                type="button"
                className="text-xs underline"
                onClick={addVariant}
                data-testid={`ep-${product.id}-add-variant`}
              >+ Add variant</button>
            </div>
            {variants.length === 0 ? (
              <p className="text-xs" style={{ color: "var(--ink-soft)" }}>
                No variants. The base price is what customers see.
              </p>
            ) : (
              <div className="space-y-2">
                {variants.map((v, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <input
                      className="input-field flex-1"
                      placeholder="e.g. 600g"
                      value={v.label}
                      onChange={(e) => setVariant(i, "label", e.target.value)}
                      data-testid={`ep-${product.id}-variant-${i}-label`}
                    />
                    <span style={{ color: "var(--ink-soft)" }}>₹</span>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      className="input-field"
                      style={{ width: "110px" }}
                      value={v.price}
                      onChange={(e) => setVariant(i, "price", e.target.value)}
                      data-testid={`ep-${product.id}-variant-${i}-price`}
                    />
                    <button
                      type="button"
                      className="text-xs underline"
                      onClick={() => removeVariant(i)}
                      data-testid={`ep-${product.id}-variant-${i}-remove`}
                    >Remove</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              className="btn-secondary text-sm"
              onClick={() => setOpen(false)}
              data-testid={`ep-${product.id}-cancel`}
            >Cancel</button>
            <button
              type="button"
              className="btn-primary text-sm"
              onClick={save}
              disabled={saving}
              data-testid={`ep-${product.id}-save`}
            >{saving ? "Saving…" : "Save"}</button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

